import { useState, useCallback, useRef } from 'react'
import { Modal } from './Modal'
import { useToast } from './Toast'
import { api, CsvPreviewResult, DirectorySyncLog } from '../lib/api'
import {
  Upload,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  Download,
  Users,
  UserPlus,
  UserCheck,
} from 'lucide-react'

interface CsvImportModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: (result: DirectorySyncLog) => void
}

type ImportStep = 'upload' | 'preview' | 'importing' | 'complete'

export function CsvImportModal({ isOpen, onClose, onSuccess }: CsvImportModalProps) {
  const [step, setStep] = useState<ImportStep>('upload')
  const [file, setFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [previewing, setPreviewing] = useState(false)
  const [previewResult, setPreviewResult] = useState<CsvPreviewResult | null>(null)
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState<DirectorySyncLog | null>(null)
  const [options, setOptions] = useState({
    update_existing: true,
    send_welcome_email: false,
    default_role: 'viewer',
  })
  const [error, setError] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const toast = useToast()

  const resetState = useCallback(() => {
    setStep('upload')
    setFile(null)
    setPreviewResult(null)
    setImportResult(null)
    setError('')
    setOptions({
      update_existing: true,
      send_welcome_email: false,
      default_role: 'viewer',
    })
  }, [])

  const handleClose = () => {
    resetState()
    onClose()
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const droppedFile = e.dataTransfer.files[0]
    if (droppedFile && droppedFile.name.endsWith('.csv')) {
      await handleFileSelect(droppedFile)
    } else {
      setError('Please upload a CSV file')
    }
  }

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      await handleFileSelect(selectedFile)
    }
  }

  const handleFileSelect = async (selectedFile: File) => {
    setFile(selectedFile)
    setError('')
    setPreviewing(true)

    try {
      const result = await api.previewCsvImport(selectedFile)
      setPreviewResult(result.data)
      setStep('preview')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to preview CSV file')
      setFile(null)
    } finally {
      setPreviewing(false)
    }
  }

  const handleImport = async () => {
    if (!file) return

    setStep('importing')
    setImporting(true)
    setError('')

    try {
      const result = await api.confirmCsvImport(file, options)
      setImportResult(result.data)
      setStep('complete')
      toast.success(`Successfully imported ${result.data.users_created + result.data.users_updated} users`)
      onSuccess?.(result.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import users')
      setStep('preview')
    } finally {
      setImporting(false)
    }
  }

  const downloadTemplate = () => {
    const template = 'email,first_name,last_name,department,role\njohn.doe@example.com,John,Doe,Engineering,viewer\njane.smith@example.com,Jane,Smith,Marketing,contributor'
    const blob = new Blob([template], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'user_import_template.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Import Users from CSV"
      maxWidth="max-w-2xl"
    >
      <div className="space-y-6">
        {/* Step: Upload */}
        {step === 'upload' && (
          <>
            {/* Download template link */}
            <div className="flex items-center justify-between text-sm">
              <p className="text-gray-500">
                Upload a CSV file with user information to import.
              </p>
              <button
                onClick={downloadTemplate}
                className="flex items-center gap-1 text-primary-600 hover:text-primary-700"
              >
                <Download className="h-4 w-4" />
                Download template
              </button>
            </div>

            {/* Dropzone */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`
                border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                ${isDragging
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
                }
                ${previewing ? 'pointer-events-none opacity-50' : ''}
              `}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileInputChange}
                className="hidden"
              />
              {previewing ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="h-10 w-10 text-primary-600 animate-spin" />
                  <p className="text-gray-600">Analyzing CSV file...</p>
                </div>
              ) : (
                <>
                  <Upload className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 font-medium">
                    Drop your CSV file here, or click to browse
                  </p>
                  <p className="text-sm text-gray-400 mt-1">
                    Supports: email, first_name, last_name, department, role columns
                  </p>
                </>
              )}
            </div>

            {/* Error message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Expected format */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Expected CSV format:</h4>
              <div className="overflow-x-auto">
                <table className="text-xs font-mono">
                  <thead>
                    <tr className="text-gray-500">
                      <th className="pr-4 pb-1 text-left">email*</th>
                      <th className="pr-4 pb-1 text-left">first_name</th>
                      <th className="pr-4 pb-1 text-left">last_name</th>
                      <th className="pr-4 pb-1 text-left">department</th>
                      <th className="pb-1 text-left">role</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-600">
                    <tr>
                      <td className="pr-4">user@example.com</td>
                      <td className="pr-4">John</td>
                      <td className="pr-4">Doe</td>
                      <td className="pr-4">Engineering</td>
                      <td>viewer</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500 mt-2">* Required field. Role can be: admin, contributor, viewer</p>
            </div>
          </>
        )}

        {/* Step: Preview */}
        {step === 'preview' && previewResult && (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <FileSpreadsheet className="h-6 w-6 text-gray-400 mx-auto mb-1" />
                <p className="text-2xl font-semibold text-gray-900">{previewResult.total_rows}</p>
                <p className="text-xs text-gray-500">Total rows</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <UserPlus className="h-6 w-6 text-green-500 mx-auto mb-1" />
                <p className="text-2xl font-semibold text-green-600">{previewResult.to_create}</p>
                <p className="text-xs text-gray-500">New users</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <UserCheck className="h-6 w-6 text-blue-500 mx-auto mb-1" />
                <p className="text-2xl font-semibold text-blue-600">{previewResult.to_update}</p>
                <p className="text-xs text-gray-500">To update</p>
              </div>
              <div className={`rounded-lg p-3 text-center ${previewResult.errors.length > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
                <AlertTriangle className={`h-6 w-6 mx-auto mb-1 ${previewResult.errors.length > 0 ? 'text-red-500' : 'text-gray-400'}`} />
                <p className={`text-2xl font-semibold ${previewResult.errors.length > 0 ? 'text-red-600' : 'text-gray-600'}`}>
                  {previewResult.errors.length}
                </p>
                <p className="text-xs text-gray-500">Errors</p>
              </div>
            </div>

            {/* Errors */}
            {previewResult.errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-red-800 mb-2 flex items-center gap-2">
                  <XCircle className="h-4 w-4" />
                  Validation Errors ({previewResult.errors.length})
                </h4>
                <div className="max-h-32 overflow-y-auto">
                  <ul className="text-sm text-red-700 space-y-1">
                    {previewResult.errors.slice(0, 10).map((err, idx) => (
                      <li key={idx}>Row {err.row}: {err.error}</li>
                    ))}
                    {previewResult.errors.length > 10 && (
                      <li className="font-medium">...and {previewResult.errors.length - 10} more errors</li>
                    )}
                  </ul>
                </div>
              </div>
            )}

            {/* Preview table */}
            {previewResult.preview.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Preview (first {Math.min(previewResult.preview.length, 10)} rows)</h4>
                <div className="border rounded-lg overflow-hidden">
                  <div className="max-h-48 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {previewResult.preview.slice(0, 10).map((user, idx) => (
                          <tr key={idx}>
                            <td className="px-3 py-2 text-gray-900">{user.email}</td>
                            <td className="px-3 py-2 text-gray-600">{user.first_name} {user.last_name}</td>
                            <td className="px-3 py-2 text-gray-600">{user.department || '-'}</td>
                            <td className="px-3 py-2">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                user.action === 'create'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {user.action === 'create' ? 'New' : 'Update'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Import options */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <h4 className="text-sm font-medium text-gray-700">Import Options</h4>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={options.update_existing}
                  onChange={(e) => setOptions({ ...options, update_existing: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Update existing users with matching email</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={options.send_welcome_email}
                  onChange={(e) => setOptions({ ...options, send_welcome_email: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Send welcome email to new users</span>
              </label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">Default role for new users:</span>
                <select
                  value={options.default_role}
                  onChange={(e) => setOptions({ ...options, default_role: e.target.value })}
                  className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="viewer">Viewer</option>
                  <option value="contributor">Contributor</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>

            {/* Error message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-between pt-4 border-t">
              <button
                onClick={resetState}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Choose different file
              </button>
              <div className="flex gap-3">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleImport}
                  disabled={previewResult.valid_rows === 0 || importing}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <Users className="h-4 w-4" />
                  Import {previewResult.valid_rows} users
                </button>
              </div>
            </div>
          </>
        )}

        {/* Step: Importing */}
        {step === 'importing' && (
          <div className="py-12 text-center">
            <Loader2 className="h-12 w-12 text-primary-600 animate-spin mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-900">Importing users...</p>
            <p className="text-sm text-gray-500 mt-1">This may take a moment</p>
          </div>
        )}

        {/* Step: Complete */}
        {step === 'complete' && importResult && (
          <div className="py-6 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-900 mb-6">Import Complete</p>

            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="bg-green-50 rounded-lg p-3">
                <p className="text-2xl font-semibold text-green-600">{importResult.users_created}</p>
                <p className="text-xs text-gray-500">Created</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-2xl font-semibold text-blue-600">{importResult.users_updated}</p>
                <p className="text-xs text-gray-500">Updated</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-2xl font-semibold text-gray-600">{importResult.users_unchanged}</p>
                <p className="text-xs text-gray-500">Unchanged</p>
              </div>
              <div className="bg-yellow-50 rounded-lg p-3">
                <p className="text-2xl font-semibold text-yellow-600">{importResult.users_disabled}</p>
                <p className="text-xs text-gray-500">Disabled</p>
              </div>
            </div>

            {importResult.errors.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6 text-left">
                <h4 className="text-sm font-medium text-yellow-800 mb-1">
                  {importResult.errors.length} rows had issues:
                </h4>
                <ul className="text-sm text-yellow-700 list-disc list-inside">
                  {importResult.errors.slice(0, 5).map((err, idx) => (
                    <li key={idx}>{err}</li>
                  ))}
                  {importResult.errors.length > 5 && (
                    <li>...and {importResult.errors.length - 5} more</li>
                  )}
                </ul>
              </div>
            )}

            <button
              onClick={handleClose}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default CsvImportModal
